import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useToast } from '../contexts/ToastContext';
import Breadcrumb from '../components/ui/Breadcrumb';
import '../styles/AddCandidate.css';

const AddCandidate = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    conferenceStatus: 'lay',
    processStage: 'inquiring',
    district: '',
    homeChurch: '',
    currentAppointment: '',
    mentor: '',
    dateOfBirth: '',
    gender: '',
    ethnicity: '',
    language: '',
    emergencyContact: '',
    emergencyPhone: '',
    education: '',
    previousExperience: '',
    notes: ''
  });

  useEffect(() => {
    fetchDistricts();
  }, []);

  const fetchDistricts = async () => {
    try {
      const districtsRef = collection(db, 'districts');
      const snapshot = await getDocs(districtsRef);
      
      const districtsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setDistricts(districtsList);
    } catch (error) {
      console.error('Error fetching districts:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const selectedDistrict = districts.find(d => d.name === formData.district);
      const candidateData = {
        ...formData,
        districtId: selectedDistrict?.id || '',
        deleted: false,
        createdAt: new Date(),
        updatedAt: new Date(),
        courseOfStudy: {
          courses: [],
          completedCredits: 0,
          totalCredits: 0
        },
        transcripts: [],
        actionReports: []
      };

      const docRef = await addDoc(collection(db, 'candidates'), candidateData);
      console.log('Candidate added with ID:', docRef.id);
      
      navigate(`/candidates/${docRef.id}`);
    } catch (error) {
      console.error('Error adding candidate:', error);
      toast.error('Error adding candidate. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-candidate-container">
      <Breadcrumb items={[
        { label: 'Dashboard', to: '/dashboard' },
        { label: 'Candidates', to: '/candidates' },
        { label: 'Add New Candidate' },
      ]} />
      <div className="add-candidate-header">
        <h1>Add New Candidate</h1>
        <button 
          type="button" 
          onClick={() => navigate('/candidates')}
          className="back-btn"
        >
          Back to Candidates
        </button>
      </div>

      <form onSubmit={handleSubmit} className="add-candidate-form">
        <div className="form-section">
          <h2>Personal Information</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="middleName">Middle Name</label>
              <input
                type="text"
                id="middleName"
                name="middleName"
                value={formData.middleName}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Last Name *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="dateOfBirth">Date of Birth</label>
              <input
                type="date"
                id="dateOfBirth"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="gender">Gender</label>
              <select
                id="gender"
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="non-binary">Non-Binary</option>
                <option value="prefer-not-to-say">Prefer Not to Say</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="ethnicity">Ethnicity</label>
              <select
                id="ethnicity"
                name="ethnicity"
                value={formData.ethnicity}
                onChange={handleInputChange}
              >
                <option value="">Select Ethnicity</option>
                <option value="african-american">African American</option>
                <option value="asian">Asian</option>
                <option value="caucasian">Caucasian</option>
                <option value="hispanic-latino">Hispanic/Latino</option>
                <option value="native-american">Native American</option>
                <option value="pacific-islander">Pacific Islander</option>
                <option value="multiracial">Multiracial</option>
                <option value="other">Other</option>
                <option value="prefer-not-to-say">Prefer Not to Say</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="language">Primary Language</label>
              <select
                id="language"
                name="language"
                value={formData.language}
                onChange={handleInputChange}
              >
                <option value="">Select Language</option>
                <option value="english">English</option>
                <option value="spanish">Spanish</option>
                <option value="french">French</option>
                <option value="korean">Korean</option>
                <option value="chinese">Chinese</option>
                <option value="vietnamese">Vietnamese</option>
                <option value="tagalog">Tagalog</option>
                <option value="portuguese">Portuguese</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Address</h2>
          <div className="form-group">
            <label htmlFor="address">Street Address</label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="city">City</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="state">State</label>
              <input
                type="text"
                id="state"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="zipCode">ZIP Code</label>
              <input
                type="text"
                id="zipCode"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Ministry Information</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="conferenceStatus">Conference Status *</label>
              <select
                id="conferenceStatus"
                name="conferenceStatus"
                value={formData.conferenceStatus}
                onChange={handleInputChange}
                required
              >
                <option value="lay">Lay</option>
                <option value="clm">Certified Lay Ministry</option>
                <option value="cmc">Certified Ministry Candidate</option>
                <option value="supply-pastor">Supply Pastor</option>
                <option value="local-pastor">Local Pastor</option>
                <option value="other-fellowship">Other Fellowship</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="processStage">Process Stage *</label>
              <select
                id="processStage"
                name="processStage"
                value={formData.processStage}
                onChange={handleInputChange}
                required
              >
                <option value="inquiring">Inquiring</option>
                <option value="certified">Certified</option>
                <option value="full-time">Full-Time</option>
                <option value="part-time">Part-Time</option>
                <option value="completed">Completed</option>
                <option value="discontinued">Discontinued</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="district">District</label>
              <select
                id="district"
                name="district"
                value={formData.district}
                onChange={handleInputChange}
              >
                <option value="">Select a district...</option>
                {districts.map(district => (
                  <option key={district.id} value={district.name}>
                    {district.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="homeChurch">Home Church</label>
              <input
                type="text"
                id="homeChurch"
                name="homeChurch"
                value={formData.homeChurch}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="currentAppointment">Current Appointment or Charge Conference</label>
              <input
                type="text"
                id="currentAppointment"
                name="currentAppointment"
                value={formData.currentAppointment}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="mentor">Mentor</label>
              <input
                type="text"
                id="mentor"
                name="mentor"
                value={formData.mentor}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Emergency Contact</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="emergencyContact">Emergency Contact Name</label>
              <input
                type="text"
                id="emergencyContact"
                name="emergencyContact"
                value={formData.emergencyContact}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="emergencyPhone">Emergency Contact Phone</label>
              <input
                type="tel"
                id="emergencyPhone"
                name="emergencyPhone"
                value={formData.emergencyPhone}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Additional Information</h2>
          <div className="form-group">
            <label htmlFor="education">Educational Background</label>
            <textarea
              id="education"
              name="education"
              value={formData.education}
              onChange={handleInputChange}
              rows="3"
            />
          </div>
          <div className="form-group">
            <label htmlFor="previousExperience">Previous Ministry Experience</label>
            <textarea
              id="previousExperience"
              name="previousExperience"
              value={formData.previousExperience}
              onChange={handleInputChange}
              rows="3"
            />
          </div>
          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows="3"
            />
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            onClick={() => navigate('/candidates')}
            className="cancel-btn"
          >
            Cancel
          </button>
          <button 
            type="submit" 
            disabled={loading}
            className="submit-btn"
          >
            {loading ? 'Adding Candidate...' : 'Add Candidate'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddCandidate;
